import { Head, Link, router, useForm } from "@inertiajs/react"
import { useRef, useState } from "react"
import { format } from "date-fns"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Checkbox } from "@/components/ui/checkbox"
import { Badge } from "@/components/ui/badge"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {
  Plus,
  Search,
  Filter,
  MoreHorizontal,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  TrendingUp,
  Upload,
} from "lucide-react"
import { AppLayout } from "@/layouts/app-layout"
import {
  incomesPath,
  newIncomePath,
  importIncomesPath,
  bulkUpdateCategoryIncomesPath,
} from "@/routes"
import type { BreadcrumbItem } from "@/types"

const breadcrumbs: BreadcrumbItem[] = [
  {
    title: "Dashboard",
    href: "/dashboard",
  },
  {
    title: "Income",
    href: incomesPath(),
  },
]

interface Income {
  id: number
  amount: number
  category: string
  description: string | null
  income_date: string
  created_at: string
  updated_at: string
}

interface IncomesIndexProps {
  incomes: Income[]
  categories: string[]
  filters: {
    category?: string
    start_date?: string
    end_date?: string
  }
  pagination: {
    current_page: number
    per_page: number
    total_count: number
  }
  summary: {
    total_amount: number
    average_amount: number
    transaction_count: number
  }
}

export default function IncomesIndex({
  incomes,
  categories,
  filters,
  pagination,
  summary,
}: IncomesIndexProps) {
  const [selectedIncomes, setSelectedIncomes] = useState<number[]>([])
  const [searchQuery, setSearchQuery] = useState("")
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleImport = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    router.post(
      importIncomesPath(),
      { file },
      { forceFormData: true, preserveScroll: true },
    )
    e.target.value = ""
  }

  const { data, setData } = useForm({
    category: filters.category ?? "all",
    start_date: filters.start_date ?? "",
    end_date: filters.end_date ?? "",
    q: searchQuery,
  })

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount)
  }

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "MMM dd, yyyy")
  }

  const handleFilter = () => {
    router.get(incomesPath(), {
      category: data.category === "all" ? "" : data.category,
      start_date: data.start_date,
      end_date: data.end_date,
      q: searchQuery,
    })
  }

  const handleSelectIncome = (incomeId: number, checked: boolean) => {
    if (checked) {
      setSelectedIncomes([...selectedIncomes, incomeId])
    } else {
      setSelectedIncomes(selectedIncomes.filter((id) => id !== incomeId))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIncomes(incomes.map((income) => income.id))
    } else {
      setSelectedIncomes([])
    }
  }

  const handleBulkDelete = () => {
    if (selectedIncomes.length === 0) return

    if (
      confirm(
        `Are you sure you want to delete ${selectedIncomes.length} income entries?`,
      )
    ) {
      router.delete(incomesPath(), {
        data: { income_ids: selectedIncomes },
      })
    }
  }

  const handleBulkUpdateCategory = (newCategory: string) => {
    if (selectedIncomes.length === 0) return

    router.patch(bulkUpdateCategoryIncomesPath(), {
      income_ids: selectedIncomes,
      category: newCategory,
    })
  }

  const filteredIncomes = incomes.filter(
    (income) =>
      (income.description?.toLowerCase().includes(searchQuery.toLowerCase()) ??
        false) ||
      income.category.toLowerCase().includes(searchQuery.toLowerCase()),
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Income" />

      <div className="flex h-full flex-1 flex-col gap-6 overflow-x-auto rounded-xl p-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Income</h1>
            <p className="text-muted-foreground">
              Track your income to measure your real savings rate
            </p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={handleImport}
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import CSV
            </Button>
            <Button asChild>
              <Link href={newIncomePath()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Income
              </Link>
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid gap-4 md:grid-cols-3">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Total Income
              </CardTitle>
              <DollarSign className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.total_amount)}
              </div>
              <p className="text-muted-foreground text-xs">
                {summary.transaction_count} entries
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                Average Amount
              </CardTitle>
              <TrendingUp className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {formatCurrency(summary.average_amount || 0)}
              </div>
              <p className="text-muted-foreground text-xs">Per entry</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">This Period</CardTitle>
              <Calendar className="text-muted-foreground h-4 w-4" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{filteredIncomes.length}</div>
              <p className="text-muted-foreground text-xs">Entries shown</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-4 w-4" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4">
              <div className="min-w-64 flex-1">
                <Label htmlFor="search">Search</Label>
                <div className="relative">
                  <Search className="text-muted-foreground absolute top-2.5 left-2 h-4 w-4" />
                  <Input
                    id="search"
                    placeholder="Search income..."
                    className="pl-8"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
              </div>

              <div className="w-48">
                <Label htmlFor="category">Category</Label>
                <Select
                  value={data.category}
                  onValueChange={(value) => setData("category", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All categories" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All categories</SelectItem>
                    {categories.map((category) => (
                      <SelectItem key={category} value={category}>
                        {category}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="w-32">
                <Label htmlFor="start_date">From</Label>
                <Input
                  id="start_date"
                  type="date"
                  value={data.start_date}
                  onChange={(e) => setData("start_date", e.target.value)}
                />
              </div>

              <div className="w-32">
                <Label htmlFor="end_date">To</Label>
                <Input
                  id="end_date"
                  type="date"
                  value={data.end_date}
                  onChange={(e) => setData("end_date", e.target.value)}
                />
              </div>

              <div className="flex items-end">
                <Button onClick={handleFilter} variant="outline">
                  Apply Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Bulk Actions */}
        {selectedIncomes.length > 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">
                    {selectedIncomes.length} entries selected
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm">
                        Update Category
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>
                      {categories.map((category) => (
                        <DropdownMenuItem
                          key={category}
                          onClick={() => handleBulkUpdateCategory(category)}
                        >
                          {category}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Delete Selected
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Income Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Income</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={
                        selectedIncomes.length === incomes.length &&
                        incomes.length > 0
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredIncomes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="py-8 text-center">
                      <div className="text-muted-foreground">
                        {incomes.length === 0
                          ? "No income yet. Add your first income entry!"
                          : "No income matches your filters."}
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredIncomes.map((income) => (
                    <TableRow key={income.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedIncomes.includes(income.id)}
                          onCheckedChange={(checked) =>
                            handleSelectIncome(income.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>{formatDate(income.income_date)}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">
                            {income.description ?? "No description"}
                          </div>
                          <div className="text-muted-foreground text-xs">
                            {format(
                              new Date(income.created_at),
                              "MMM dd, yyyy",
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{income.category}</Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(income.amount)}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem asChild>
                              <Link href={`/incomes/${income.id}/edit`}>
                                <Edit className="mr-2 h-4 w-4" />
                                Edit
                              </Link>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-destructive"
                              onClick={() => {
                                if (
                                  confirm(
                                    "Are you sure you want to delete this income entry?",
                                  )
                                ) {
                                  router.delete(`/incomes/${income.id}`)
                                }
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {/* Pagination */}
            {pagination.total_count > pagination.per_page && (
              <div className="flex items-center justify-between pt-4">
                <div className="text-muted-foreground text-sm">
                  Showing{" "}
                  {(pagination.current_page - 1) * pagination.per_page + 1} to{" "}
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total_count,
                  )}{" "}
                  of {pagination.total_count} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pagination.current_page === 1}
                    onClick={() =>
                      router.get(incomesPath(), {
                        ...data,
                        page: pagination.current_page - 1,
                      })
                    }
                  >
                    Previous
                  </Button>
                  <span className="text-sm">
                    Page {pagination.current_page}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={
                      pagination.current_page * pagination.per_page >=
                      pagination.total_count
                    }
                    onClick={() =>
                      router.get(incomesPath(), {
                        ...data,
                        page: pagination.current_page + 1,
                      })
                    }
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  )
}
